<template>
  <div class="health-wrapper">
    <div class="container-fluid py-5 health-content">
      <h2 class="mb-4">System Health</h2>

      <section class="mb-5">
        <h5 class="text-muted mb-3">Frontend</h5>
        <table class="table table-sm table-bordered bg-white">
          <colgroup>
            <col class="col-label" />
            <col />
          </colgroup>
          <tbody>
            <tr>
              <th scope="row">Version</th>
              <td>{{ frontendVersion }}</td>
            </tr>
            <tr>
              <th scope="row">Environment</th>
              <td>{{ frontendEnv }}</td>
            </tr>
            <tr>
              <th scope="row">Git SHA</th>
              <td><code>{{ frontendSha }}</code></td>
            </tr>
            <tr>
              <th scope="row">Build Time</th>
              <td>{{ frontendBuildTime }}</td>
            </tr>
            <tr>
              <th scope="row">Loaded</th>
              <td>{{ loadedAt }}</td>
            </tr>
            <tr>
              <th scope="row">API URL</th>
              <td>{{ apiBaseUrl }}</td>
            </tr>
          </tbody>
        </table>
      </section>

      <section>
        <h5 class="text-muted mb-3">
          API
          <span v-if="loading" class="spinner-border spinner-border-sm ms-2" role="status" />
        </h5>

        <div v-if="error" class="alert alert-danger">{{ error }}</div>

        <table v-else class="table table-sm table-bordered bg-white">
          <colgroup>
            <col class="col-label" />
            <col />
            <col class="col-status" />
          </colgroup>
          <tbody>
            <tr>
              <th scope="row">Status</th>
              <td>{{ apiHealth?.status ?? "—" }}</td>
              <td class="text-center">
                <span :class="statusBadgeClass(apiHealth?.status)">
                  {{ apiHealth?.status ?? "—" }}
                </span>
              </td>
            </tr>
            <tr>
              <th scope="row">Version</th>
              <td colspan="2">{{ apiHealth?.version ?? "—" }}</td>
            </tr>
            <tr>
              <th scope="row">Git SHA</th>
              <td colspan="2">
                <code>{{ apiHealth?.build_sha ?? "—" }}</code>
              </td>
            </tr>
            <tr>
              <th scope="row">Environment</th>
              <td colspan="2">{{ apiHealth?.environment ?? "—" }}</td>
            </tr>
            <tr>
              <th scope="row">Build Time</th>
              <td colspan="2">{{ apiHealth?.build_timestamp ?? "—" }}</td>
            </tr>
            <tr>
              <th scope="row">Uptime</th>
              <td colspan="2">{{ apiUptime }}</td>
            </tr>
            <tr v-for="(svc, name) in apiHealth?.services" :key="name">
              <th scope="row">{{ name }}</th>
              <td>{{ svc.detail ?? "—" }}</td>
              <td class="text-center">
                <span :class="statusBadgeClass(svc.status)">{{ svc.status }}</span>
              </td>
            </tr>
          </tbody>
        </table>

        <button
          class="btn btn-sm btn-outline-secondary mt-2"
          :disabled="loading"
          @click="fetchHealth"
        >
          Refresh
        </button>
      </section>
    </div>
  </div>
</template>

<script>
export default {
  name: "HealthPage",

  data() {
    return {
      loading: true,
      error: null,
      apiHealth: null,
      loadedAt: new Date().toLocaleString(),
    };
  },

  computed: {
    frontendVersion() {
      return process.env.VUE_APP_VERSION || "unknown";
    },
    frontendEnv() {
      return process.env.VUE_APP_ENV || "unknown";
    },
    frontendSha() {
      return process.env.VUE_APP_GIT_SHA || "unknown";
    },
    frontendBuildTime() {
      return process.env.VUE_APP_BUILD_TIMESTAMP || "unknown";
    },
    apiBaseUrl() {
      return process.env.VUE_APP_API_BASE_URL || "unknown";
    },
    apiUptime() {
      const seconds = this.apiHealth?.uptime_seconds;
      if (seconds == null) return "—";
      const h = Math.floor(seconds / 3600);
      const m = Math.floor((seconds % 3600) / 60);
      const s = seconds % 60;
      if (h > 0) return `${h}h ${m}m ${s}s`;
      if (m > 0) return `${m}m ${s}s`;
      return `${s}s`;
    },
  },

  mounted() {
    this.fetchHealth();
  },

  methods: {
    async fetchHealth() {
      this.loading = true;
      this.error = null;
      try {
        const res = await fetch(`${this.apiBaseUrl}/health`);
        this.apiHealth = await res.json();
      } catch (e) {
        this.error = `Could not reach API: ${e.message}`;
      } finally {
        this.loading = false;
      }
    },

    statusBadgeClass(status) {
      return {
        badge: true,
        "bg-success": status === "ok",
        "bg-danger": status === "error" || status === "degraded",
        "bg-secondary": !status,
      };
    },
  },
};
</script>

<style scoped>
.health-wrapper {
  min-height: 100vh;
  background-color: #e7e3d4;
  text-align: left;
}

.health-content {
  max-width: 800px;
  margin: 0 auto;
}

.col-label {
  width: 140px;
}

.col-status {
  width: 80px;
}

th[scope="row"] {
  text-align: left;
}

td {
  text-align: left;
}
</style>
